import dayjs, { Dayjs } from 'dayjs'
import { TimeZone } from 'types/graphql'

import { getDayjsInTimeZone } from 'src/utils/date'

import { DateRangePickerValue, DateRangeType } from '../DateRangePicker/DateRangePicker'

export const getPresetDateRange = (
  type: DateRangeType,
  timeZone?: TimeZone
): { startDate: Dayjs; endDate: Dayjs } => {
  const today = timeZone
    ? getDayjsInTimeZone(dayjs(), timeZone).startOf('day')
    : dayjs().startOf('day')
  switch (type) {
    case DateRangeType.Today:
      return { startDate: today, endDate: today }
    case DateRangeType.Yesterday: {
      const d = today.subtract(1, 'day')
      return { startDate: d, endDate: d }
    }
    case DateRangeType.PastWeek:
      return { startDate: today.subtract(7, 'day'), endDate: today }
    case DateRangeType.MonthToDate:
      return { startDate: today.startOf('month'), endDate: today }
    case DateRangeType.Past4Weeks:
      return { startDate: today.subtract(28, 'day'), endDate: today }
    case DateRangeType.Past12Weeks:
      return { startDate: today.subtract(84, 'day'), endDate: today }
    case DateRangeType.YearToDate:
      return { startDate: today.startOf('year'), endDate: today }
    case DateRangeType.Past6Months:
      return { startDate: today.subtract(6, 'month'), endDate: today }
    case DateRangeType.Past12Months:
      return { startDate: today.subtract(1, 'year'), endDate: today }
    default:
      return { startDate: today, endDate: today }
  }
}

export const getDateRangePickerValue = (
  type: DateRangeType,
  timeZone?: TimeZone
): DateRangePickerValue => {
  if (type === DateRangeType.Custom) {
    return { type, startDate: undefined, endDate: undefined }
  }
  const { startDate, endDate } = getPresetDateRange(type, timeZone)
  return { type, startDate, endDate }
}

export const getDateRangeDisplay = (
  startDate: Dayjs | undefined,
  endDate: Dayjs | undefined,
  timeZone?: TimeZone
) => {
  if (!startDate && !endDate) {
    return ''
  }

  const start = startDate ? getDayjsInTimeZone(startDate, timeZone) : undefined
  const end = endDate ? getDayjsInTimeZone(endDate, timeZone) : undefined

  const currentYear = getDayjsInTimeZone(dayjs(), timeZone).year()
  const sameYear = start && end && start?.year() === end?.year()
  const sameMonth = sameYear && start?.month() === end?.month()
  const sameDay = sameMonth && start?.date() === end?.date()

  let startFormat = 'MMMM D'
  let endFormat = 'MMMM D'

  if (sameDay) {
    if (start.year() !== currentYear) {
      startFormat += ', YYYY'
    }
    return start.format(startFormat)
  }

  if (sameMonth) {
    // if the start date and end date are in the same month, only show the day for the end date
    endFormat = 'D'
  }

  if (!sameYear) {
    // if the start date is in a different year than the end date, add the year to the format
    startFormat += ', YYYY'
    endFormat += ', YYYY'
  } else if (start?.year() !== currentYear) {
    // if the years are the same, but the start date is in a different year than the current year, add the year to the format
    endFormat += ', YYYY'
  }

  return `${start?.format(startFormat) ?? 'Not set'} - ${end?.format(endFormat) ?? 'Not set'}`
}
